import React from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import UserInfo from '../../UserInfo';
import Enums from '../../enums';
import { Translate } from '../../Utils/Languages/Translate';
import { isMobile, trakrefFontFamily, trakrefFontFamilySemiBold } from '../../utils';
import { Grey3, trakrefBlue } from '../../color_utils';
import { determineBadgeColor } from "../Common/DataGrid/utils";
import { canRecordInstall } from "../Account/utils";

export const problem_resolve_options = [
    { value: true, name: 'Yes' },
    { value: false, name: 'No' }
];

export const status_after_service_options = [
    { value: 'Active', name: 'Active' },
    { value: 'Inactive', name: 'Inactive' },
    { value: 'Decommissioned', name: 'Decommissioned' }
];

export const gas_columns = [
    { name: Translate("rapidCylinderEntry.cylinderRepeater.cylinder-name") },
    { name: Translate("recoverGas.transfer") },
    { name: Translate("rapidCylinderEntry.cylinderInputForm.reason-for-recover-gas") },
    { name: Translate("recoverGas.showRecoveryCylinderDetails.cylinder-notes") },
    { name: Translate("installGas.tranfer-notes") },
    { name: " " }
];

export const install_gas_columns = [
    { name: Translate("rapidCylinderEntry.cylinderRepeater.cylinder-name") },
    { name: Translate("recoverGas.transfer") },
    { name: Translate("rapidCylinderEntry.cylinderInputForm.material-status") },
    { name: Translate("recoverGas.showRecoveryCylinderDetails.cylinder-notes") },
    { name: Translate("installGas.tranfer-notes") },
    { name: "" }
];

export const yes_no_options = [
    { id: true, name: Translate("yes") },
    { id: false, name: Translate("no") },
];

export const oil_type_options = [
    { value: "0", name: "AB" },
    { value: "1", name: "POE" },
    { value: "2", name: "Mineral" }
];

// Real cylinders show the server-built "[Name] / [Material] / [Weight]" label
// (CylinderDropDownModel.Label); when the cylinder has no material type the empty middle
// segment reads "(No Material)". The Unknown cylinder (id 0) and edit-mode rows hydrated
// from a saved transfer carry no label and fall back to name.
export const cylinderOptionLabel = (option) => {
    if (!option) return '';
    if (!option.label) return option.name || '';
    if (option.id !== 0 && !option.materialType) {
        return option.label.replace('/  /', '/ (No Material) /');
    }
    return option.label;
};

// Scan candidates must show the tag (US 16907 AC3); kept out of the label so `label` stays searchable.
export const CylinderOption = (props, option) => (
    <li {...props} key={option.id}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', minWidth: 0 }}>
            <Typography
                component="span"
                sx={{ flex: 1, minWidth: 0, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
                {cylinderOptionLabel(option)}
            </Typography>
            {option.tagNumber && (
                <Typography component="span" sx={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.6)', flexShrink: 0 }}>
                    {option.tagNumber}
                </Typography>
            )}
        </Box>
    </li>
);

// These are the default required fields. Required fields may change based on user's answers
// See individual components for conditional required fields
export const serviceEventTypesRequiredFields = {
    [Enums.SE.WorkItemType.MaintenanceNoGas]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
        ],
        "RepairDetails": [
            "leakLocationCategoryId",
            "repairNotes",
        ],
        "ProblemResolution": [],
    },
    [Enums.SE.WorkItemType.LeakInspection]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
        ],
        "RepairDetails": [
            "leakDetectionMethodId",
            "wasLeakFound",
        ]
    },
    [Enums.SE.WorkItemType.ServiceOrLeakRepair]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
        ],
        "RepairDetails": [
            "leakDetectionMethodId",
            "wasLeakFound",
        ],
        "InitialVerificationTest": [
            "initialVerificationLeakInspection.leakDetectionMethodId",
            "wasLeakFound",
        ],
        "RepairNotes": [
            "repairNotes",
            "serviceActionId"
        ],
        "GasTransaction": [],
        "FollowUpVerificationService": [
            "inspectionDate",
            "leakDetectionMethodId",
            "wasLeakFound",
        ]
    },
    [Enums.SE.WorkItemType.Install]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
            "serviceActionId",
            "repairNotes"
        ],
        "GasTransaction": [],
        "VerificationTests": [
            "currentGasWeightLbs",
            "chargeCapacitySourceTypeId"
        ]
    },
    [Enums.SE.WorkItemType.ShutdownMothball]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
            "serviceActionId",
        ],
        "GasTransaction": ["wasProperVacuumPulled"],
        "PostShutdownStatus": ["finalCoolingApplianceStatusId"],
    },
    [Enums.SE.WorkItemType.Retrofit]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
            "serviceActionId",
            "repairNotes"
        ],
        "NewSystemDetails": [
            "newMaterialTypeId",
            "newOilType",
            "newGasChargeCapacityLbs"
        ],
        "GasTransaction": [],
        "VerificationTests": [
            "verificationLeakDetectionMethodId",
            "chargeCapacitySourceTypeId"
        ]
    },
    [Enums.SE.WorkItemType.LeakMonitorCalibration]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
            "alarmSetPoint",
            "detectionLimit"
        ]
    },
    [Enums.SE.WorkItemType.Reinstall]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
            "serviceActionId",
            "repairNotes"
        ],
        "GasTransaction": [],
        "VerificationTests": [
            "currentGasWeightLbs",
            "chargeCapacitySourceTypeId"
        ]
    },
    [Enums.SE.WorkItemType.RecordRefrigerant]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
        ],
        "RepairDetails": [
            "notes"
        ],
        "GasTransaction": [],
    },
    [Enums.SE.WorkItemType.AirFilterChangeOut]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
        ],
    },
    [Enums.SE.WorkItemType.Move]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
            "newLocationId",
        ],
    },
    [Enums.SE.WorkItemType.RecordVitalStats]: {
        "EnterService": [
            "assetId",
            "primaryTechnicianId",
            "serviceDate",
        ],
    }
};

export const allServiceEventSteps = {
    // 8 - Quaretly Leak Inspection
    // 9 - Annual Leak Inspection
    // 10 - Monthly Leak Inspection

    [Enums.SE.WorkItemType.MaintenanceNoGas]: [Translate("EnterService"), Translate("RepairDetails"), Translate("ProblemResolution"), Translate("Completion")],
    [Enums.SE.WorkItemType.LeakInspection]: [Translate("EnterService"), Translate("RepairDetails"), Translate("Completion")],
    [Enums.SE.WorkItemType.ServiceOrLeakRepair]: [Translate("EnterService"), Translate("RepairDetails"), Translate("RepairNotes"), Translate("InitialVerificationTest"), Translate("GasTransaction"), Translate("Completion")],
    [Enums.SE.WorkItemType.Install]: [Translate("EnterService"), Translate("VerificationTests"), Translate("GasTransaction"), Translate("Completion")],
    [Enums.SE.WorkItemType.ShutdownMothball]: [Translate("EnterService"), Translate("GasTransaction"), Translate("PostShutdownStatus"), Translate("Completion")],
    [Enums.SE.WorkItemType.Retrofit]: [Translate("EnterService"), Translate("NewSystemDetails"), Translate("VerificationTests"), Translate("GasTransaction"), Translate("Completion")],
    [Enums.SE.WorkItemType.LeakMonitorCalibration]: [Translate("EnterService")], // Leak Monitor Calibration does not require any additional screens. only additional fields within the Enter Service Screen.
    8: [Translate("EnterService")],
    9: [Translate("EnterService")],
    10: [Translate("EnterService")],
    [Enums.SE.WorkItemType.Reinstall]: [Translate("EnterService"), Translate("VerificationTests"), Translate("GasTransaction"), Translate("Completion")],
    [Enums.SE.WorkItemType.RecordVitalStats]: [Translate("EnterService"), Translate("Completion")],
    [Enums.SE.WorkItemType.RecordRefrigerant]: [Translate("EnterService"),  Translate("RepairDetails"), Translate("GasTransaction"), Translate("Completion")],
    [Enums.SE.WorkItemType.Reinstall]: [Translate("EnterService"), Translate("InstallGas"), Translate("VerificationTests"), Translate("Completion")],
    [Enums.SE.WorkItemType.RecordRefrigerant]: [Translate("EnterService"),  Translate("RepairDetails"), Translate("RecoverGas"), Translate("InstallGas"), Translate("Completion")],
    14: [Translate("EnterService")],
    [Enums.SE.WorkItemType.ServiceOrLeakRepair2]: [Translate("EnterService")],
    [Enums.SE.WorkItemType.Decomissioned]: [Translate("EnterService")],
    [Enums.SE.WorkItemType.AirFilterChangeOut]: [Translate("EnterService"), Translate("Completion")],
    [Enums.SE.WorkItemType.Move]: [Translate("EnterService"), Translate("Completion")],

/*    These steps needs to translated so it can render the translated text in the stepper, for comparison use 
 *    serviceEventSteps[activeStep].props.id === 'TabNameTranslationID'*/
};

export const recoverGasTableColumns = [
    { name: Translate("propertyUseCreateView.name") },
    { name: Translate("installGas.transfer-amount") },
    { name: Translate("rapidCylinderEntry.cylinderInputForm.reason-for-recover-gas") },
    { name: Translate("installGas.cylinder-notes") },
    { name: Translate("installGas.tranfer-notes") },
];

export const installGasTableColumns = [
    { name: Translate("propertyUseCreateView.name") },
    { name: Translate("installGas.transfer-amount") },
    { name: Translate("rapidCylinderEntry.cylinderInputForm.material-status") },
    { name: Translate("installGas.cylinder-notes") },
    { name: Translate("installGas.tranfer-notes") },
];

// this needs to be inverted because the question asks..
// "Leak Test Pass or Fail"
// and we store the value in WasLeakFound
// so if it passed there was no leak found
export const testResultsOptions = [
    { id: false, name: Translate("pass") }, 
    { id: true, name: Translate("fail") }
];

export const badValuesArray = [undefined, null, ""];

export const unknownCylinder = [{
    name: 'Unknown Cylinder',
    id: 0,
    materialType: null,
    materialTypeId: null,
    currentGasWeightLbs: 0,
    maxGasCapacityLbs: null,
}];

export const initialServiceEventsState = {
    workItemTypeId: "",
    assetId: "",
    subsystemId: "",
    primaryTechnicianId: "",
    secondaryTechnicianId: "",
    repairOutcomeTypeId: null,
    userWeightUnits: "",
    serviceDate: null,
    repairNotes: "",
    r3Filed: null,
    recoveries: [],
    installs: [],
    gasChargeCapacity: null,
    finalModalOpen: false,
    initialLeakInspection: { inspectionDate: null },
    initialVerificationLeakInspection: { inspectionDate: null },
    verificationLeakInspection: { inspectionDate: null },
    maintenanceNoGas: {},
    saveForLaterShowAgain: false,
    maxViewedStepIndex: 0,
    wasGasTransferred: true,
};

export const initialRequiredFieldsMissing = {
    initialLeakInspection: {},
    initialVerificationLeakInspection: {},
    verificationLeakInspection: {},
    maintenanceNoGas: {},
};

export const initialVitalStatState = {
    loading: false,
    inputFields: [],
    technicianUserId: "",
    values: [],
    errors: {},
    warning: false,
    tableHeaders: ['Stat Name', 'Stat Value', 'Collection Date', 'Technician Name', 'Notes'],
    tableValues: null,
};

export const reverseServiceEventEnums = {
    1: "Maintenance No Gas",
    2: "Leak Inspection",
    3: "Service or Leak Repair",
    4: "Install",
    5: "Shutdown/Mothball",
    6: "Retrofit",
    7: "Leak Monitor Calibration",
    8: "Leak Inspection 2",
    11: "Reinstall",
    12: "Record Vital Stats",
    13: "Record Refrigerant",
    15: "Service or Leak Repair 2",
    16: "Decomissioned",
    17: "Air Filter Change Out",
    18: "Move"
};

export const getLaborDropdownValues = (hourValue = "") => {
    if (hourValue !== "") {
        const hours = Math.floor(hourValue);
        const mins = hours * 60;
        return { id: hourValue * 60, label: `${hours} hrs. ${mins} min.` };
    }
    const list = [];
    const getTimeDisplay = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes - hours * 60;
        const val = `${hours} hrs. ${mins} min.`;
        return val;
    };
    // 15 minute increments
    for (var i = 0; i < 60 * 24; i = i + 15) {
        list.push({ id: i, label: getTimeDisplay(i) });
    }
    return list;
};

export const approvedServiceEventTypes = [
    Enums.SE.WorkItemType.Retrofit,
    Enums.SE.WorkItemType.LeakInspection,
    Enums.SE.WorkItemType.ServiceOrLeakRepair,
    Enums.SE.WorkItemType.AirFilterChangeOut,
    Enums.SE.WorkItemType.ShutdownMothball,
    Enums.SE.WorkItemType.Install,
    Enums.SE.WorkItemType.Reinstall,
    Enums.SE.WorkItemType.MaintenanceNoGas,
    Enums.SE.WorkItemType.RecordRefrigerant,
    Enums.SE.WorkItemType.RecordVitalStats,
    Enums.SE.WorkItemType.LeakMonitorCalibration,
];

export const approvedServiceEventTypesPWA = [
    Enums.SE.WorkItemType.LeakInspection,
    Enums.SE.WorkItemType.ServiceOrLeakRepair,
    Enums.SE.WorkItemType.Retrofit,
];

export const underRepairCoolingApplianceServiceEventTypes = [2, 3, 5, 6, 13];

export const filterTechniciansForService = (asset, technicians, california = false) => {
    //So there are certication types that we store bitwise cause we are so damn smart...
    //If you dont know bitwise, basically each bit in an integer is its own flag
    //Example: Types 1 and 3 would be 00101 (or 5), while all would be 11111 (31)
    //You can look at the GetCertificationTypes function in AccountUtl for some more info

    return technicians.filter(e =>
        //Type 1 certification (bit 1) allows work on Assets under 5 lbs
        ((e.certificationTypes & 1)
            && (asset != null)
            && (asset.maxGasCapacity < 5)
            && (asset.materialTypeName !== 'SF6'))
        ||
        //Type 2 certification allows users to work on High Pressure Systems
        ((e.certificationTypes & 2)
            && (asset != null)
            && (asset.maxGasCapacity >= 5)
            && (asset.materialTypeName !== 'SF6'))
        ||
        //Type 3 certification is a special type that allows work on Low Pressure Systems
        ((e.certificationTypes & 4)
            && (asset != null)
            && (asset.materialTypeName === 'R-11'
                || asset.materialTypeName === 'R-113'
                || asset.materialTypeName === 'R-123'))
        ||
        //We use the 4th bit for Universal certification
        ((e.certificationTypes & 8)
            && (asset != null)
            && (asset.materialTypeName !== 'SF6'))
        ||
        //We use the fifth bit for SF6 certification
        ((e.certificationTypes & 16)
            && (asset != null)
            && (asset.materialTypeName === 'SF6')))
        //Make sure tech is enabled
        .filter(t => t.enabled === true)
        //California only explicitly allows Technicians with a valid cert number
        .filter(x => california === false
            || x.groupID !== 6
            || (
                california === true
                && x.groupID === 6
                && x.validCertificateNumber === true
            )
        );
};

export const getNormalOperationServiceOptions = (asset, types, serviceEventId) => (
    types
    .filter(x => x.id !== Enums.SE.WorkItemType.LeakMonitorCalibration)
    .filter(x => (serviceEventId || (!serviceEventId && x.id !== Enums.SE.WorkItemType.Install)))
    .filter((x) =>
        (
            !serviceEventId
            && asset.coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.PlannedRetrofit
            && x.id === Enums.SE.WorkItemType.Retrofit
        )
        || (
            !serviceEventId
            && asset.coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.Mothballed
            && (
                x.id < Enums.SE.WorkItemType.LeakInspection2
                || x.id > 10 // 10 does not exist as a type in V4
            )
            && x.id !== Enums.SE.WorkItemType.ShutdownMothball // Allow Reinstall for Mothballed assets
        )
        || (
            !serviceEventId
            && asset.coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.ShutdownRetired
            && (
                x.id < Enums.SE.WorkItemType.LeakInspection2
                || x.id > Enums.SE.WorkItemType.Reinstall
            )
            && x.id !== Enums.SE.WorkItemType.ShutdownMothball // Do not allow Reinstall for Shutdown assets
        )
        || (
            !serviceEventId
            && asset.coolingApplianceStatusId !== Enums.SE.CoolingApplianceStatus.PlannedRetrofit
            && asset.coolingApplianceStatusId !== Enums.SE.CoolingApplianceStatus.ShutdownRetired
            && asset.coolingApplianceStatusId !== Enums.SE.CoolingApplianceStatus.Mothballed
            && (
                x.id < Enums.SE.WorkItemType.LeakInspection2
                || x.id > Enums.SE.WorkItemType.Reinstall
            )
        )
        || serviceEventId
    )
    .filter(x =>
        serviceEventId
        || (!serviceEventId && (asset.canRetrofit || (!asset.canRetrofit && x.id !== Enums.SE.WorkItemType.Retrofit)))
    )
    .filter(x =>
        (
            x.id === Enums.SE.WorkItemType.RecordRefrigerant
            && asset.gasChargeCapacityLbs >= 0
        )
        || x.id !== Enums.SE.WorkItemType.RecordRefrigerant
    )
    .filter(x =>
        (UserInfo.instanceId === UserInfo.nativeInstanceId)
        || serviceEventId
        || (
            UserInfo.isSystemAdministrator()
            || UserInfo.isNativeTenantAdministrator()
            || UserInfo.isNativeSuperTenantAdministrator
        )
    )
    .filter(x =>
        (
            (
                asset.isHermeticallySealed
                || (
                    asset.coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.Mothballed
                    || asset.coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.ShutdownRetired
                )
            )
            && (
                asset.childrenSubsystemsCount == null
                || asset.childrenSubsystemsCount === 0
                || serviceEventId
            )
            && x.id === Enums.SE.WorkItemType.Move
        )
        || x.id !== Enums.SE.WorkItemType.Move
    )
)

export const getUnderRepairCoolingApplianceServiceOptions = (asset, types, serviceEventId) => (
    types
        .filter(x => underRepairCoolingApplianceServiceEventTypes.includes(x.id))
        .filter(x =>
            serviceEventId
            || (!serviceEventId && (asset.canRetrofit || (!asset.canRetrofit && x.id !== Enums.SE.WorkItemType.Retrofit)))
        )
        .filter(x =>
            (UserInfo.instanceId === UserInfo.nativeInstanceId)
            || serviceEventId
            || (
                UserInfo.isSystemAdministrator()
                || UserInfo.isNativeTenantAdministrator()
                || UserInfo.isNativeSuperTenantAdministrator()
            )
            || (
                UserInfo.instanceId !== UserInfo.nativeInstanceId
                && !serviceEventId
                && x.id !== Enums.SE.WorkItemType.Retrofit
            )
        )
);

export const getNonCoolingApplianceServiceOptions = (types) => (
    types.filter(x => x.id === Enums.SE.WorkItemType.LeakMonitorCalibration)  
);

export const sortServiceEventOptions = options => options.sort((a, b) => a.name.localeCompare(b.name))

export const determineServiceOptions = (assetUnderRepair, asset, types, serviceEventId) => {
    //Cooling Appliance Options
    let serviceEventOptions;

    if (asset.coolingApplianceStatusId !== null && !(
            asset.assetTypeId === Enums.AssetTypes.ControlsControl ||
            asset.assetTypeId === Enums.AssetTypes.LeakMonitoringDevice
        )
    ) {
        if (assetUnderRepair) {
            return getUnderRepairCoolingApplianceServiceOptions(asset, types, serviceEventId);
        }

        serviceEventOptions = getNormalOperationServiceOptions(asset, types, serviceEventId);

        // #15388: a restricted account offers Install only to owner/operator admins (server enforces the same)
        if (asset.coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.ProvisionalNonOperation && canRecordInstall()) {
            const installGasServiceEventOption = types.find(o => o.id === Enums.SE.WorkItemType.Install);
            serviceEventOptions.push(installGasServiceEventOption);
        }
    } else {
        serviceEventOptions = getNonCoolingApplianceServiceOptions(types);
    }

    return sortServiceEventOptions(serviceEventOptions);
};

export const getTotalGasAmount = arr => arr
    .filter(x => x.enabled)
    .reduce((total, row) => total + row.amount, 0);

export const menuProps = {
    overflowY: 'scroll',
    PaperProps: {
        style: {
            maxHeight: 48 * 4.5 + 16,
            // maxWidth: 850,
        },
        sx: {
            "& .MuiMenuItem-root": {
                whiteSpace: "normal",     // allow wrapping
                wordBreak: "break-word",  // break long words
            }
        }
    },
};

export const enabledServiceEventTypes = [
    Enums.SE.WorkItemType.LeakInspection,
    Enums.SE.WorkItemType.ServiceOrLeakRepair,
    Enums.SE.WorkItemType.Retrofit,
    Enums.SE.WorkItemType.AirFilterChangeOut,
    Enums.SE.WorkItemType.ShutdownMothball,
    Enums.SE.WorkItemType.Install,
    Enums.SE.WorkItemType.Reinstall,
    Enums.SE.WorkItemType.LeakMonitorCalibration,
    Enums.SE.WorkItemType.MaintenanceNoGas,
    Enums.SE.WorkItemType.RecordRefrigerant,
    Enums.SE.WorkItemType.Move,
    Enums.SE.WorkItemType.RecordVitalStats,
];

export const formatDataRetrofit = (data_obj) => {
    const {
        serviceActionId = '',
        wasProperVacuumPulledInt = null,
        wasProperVacuumPulled,
        vacuumPsi = '',
        recoveryEquipmentId = '',
        repairNotes = '',
        partsRequired = '',
        verificationLeakDetectionMethodId = '',
        newMaterialTypeId = '',
        newOilType = '',
        newGasChargeCapacityLbs = '',
        chargeCapacitySourceTypeId,
        initialLeakInspection,
        initialVerificationLeakInspection,
        verificationLeakInspection,
        finalCoolingApplianceStatusId,
        serviceEventWorkItemPartsRequired,
        newOilTypeId,
        gasChargeCapacity,
        assetDetails,
        finalModalOpen,
        installGasModalOpen,
        recoverGasModalOpen,
        ...rest
    } = data_obj;
    const coolingApplianceService = {
        serviceActionId,
        wasProperVacuumPulledInt,
        wasProperVacuumPulled,
        vacuumPsi,
        finalCoolingApplianceStatusID: 1,
        recoveryEquipmentId,
        partsRequired,
        verificationLeakDetectionMethodId,
        newMaterialTypeId,
        newOilType,
        newGasChargeCapacityLbs,
        chargeCapacitySourceTypeId
    };

    return {
        ...rest,
        repairNotes,
        coolingApplianceService
    };
};

export const formatDataShutdownMothball = (data_obj) => {
    const {
        serviceActionId = null,
        wasProperVacuumPulled = null,
        vacuumPsi = null,
        finalCoolingApplianceStatusId = null,
        recoveryEquipmentId,
        initialLeakInspection,
        initialVerificationLeakInspection,
        verificationLeakInspection,
        wasProperVacuumPulledInt,
        assetDetails,
        finalModalOpen,
        installGasModalOpen,
        repairNotes,
        ...rest
    } = data_obj;
    const coolingApplianceService = {
        serviceActionId,
        wasProperVacuumPulled,
        vacuumPsi,
        finalCoolingApplianceStatusId,
        recoveryEquipmentId,
    };

    return { ...rest, 
        coolingApplianceService, 
        notes: data_obj.repairNotes 
    };
};

export const formatDataLeakInspection = (data_obj, isEditing) => {
    const {
        serviceActionId,
        wasProperVacuumPulled,
        vacuumPsi,
        finalCoolingApplianceStatusId,
        repairNotes,
        initialLeakInspection,
        initialVerificationLeakInspection,
        verificationLeakInspection,
        wasProperVacuumPulledInt,
        assetDetails,
        finalModalOpen,
        installGasModalOpen,
        ...rest
    } = data_obj;

    return {
        ...rest,
        leakInspection: {
            leakInspection: { ...initialLeakInspection },
            ID: isEditing ? initialLeakInspection.id : ""
        }
    };
};

// Derive the Leak Repair Status (leakRepairDispositionTypeId) from verification test results
// instead of a manual dropdown (#14798, Spike #14793 — Flow B: verification RESULTS are the source
// of truth for disposition, not the user's Repair Outcome selection). Inputs:
//   ivtRendered — is the Initial Verification Test rendered/active? (false when Repair Outcome =
//                 No Repair Attempted, so no verification tests were performed)
//   ivtResult   — IVT "was leak found?": true = Fail, false = Pass, null/undefined = not answered
//   fvtResult   — FVT "was leak found?": true = Fail, false = Pass, null/undefined = pending / no FVT
// Returns Enums.SE.LeakRepairDispositionType (1 NoRepair / 2 RepairAttempted / 3 LeakRepaired) or
// null when the outcome cannot be determined yet (IVT Pass but FVT not completed — Save For Later).
export const deriveLeakRepairDisposition = (ivtRendered, ivtResult, fvtResult) => {
    const { NoRepair, RepairAttempted, LeakRepaired } = Enums.SE.LeakRepairDispositionType;

    // No verification tests performed (No Repair Attempted) → no repair.
    if (!ivtRendered) return NoRepair;

    // Initial verification failed (leak still found) → repair attempted, not confirmed. When IVT
    // fails the FVT step is removed, so there is no follow-up result to consider.
    if (ivtResult === true) return RepairAttempted;

    // Initial verification passed (no leak found) → disposition hinges on the follow-up result.
    if (ivtResult === false) {
        if (fvtResult === false) return LeakRepaired;    // FVT Pass → both verifications confirm repair
        if (fvtResult === true) return RepairAttempted;  // FVT Fail → initial held, follow-up failed
        return null;                                     // FVT pending (Save For Later)
    }

    // IVT rendered but not yet answered → cannot derive yet.
    return null;
};

export const formatDataServiceOrLeakRepair = (data_obj, isSaveForLater = false) => {
    const {
        verificationLeakInspection: {
            leakRepairDispositionTypeId = null,
            ...verificationLeakInspectionRest
        } = {},
        ...rest
    } = data_obj;

    let returnObject = {
        ...rest,
        leakRepairDispositionTypeId,
        verificationLeakInspection: { ...verificationLeakInspectionRest }
    };

    // need to create these 2 leak inspection objects..
    // ..in order to edit this draft service event at a later time
    if (isSaveForLater) {
        returnObject = {
            ...returnObject,
            initialLeakInspection: {},
            initialVerificationLeakInspection: {},
        };
    }

    // Safety net: if NO leak was found at discovery (initialLeakInspection.wasLeakFound is
    // false), null out the stale discovery-leak DETAIL fields (location / cause / amount) that
    // may have persisted from a previous save when the user skipped tabs.
    //
    // We deliberately do NOT touch the Initial Verification Test / Follow-Up Verification RESULTS
    // (wasLeakFound / leakDetectionMethodId / inspectionDate). Those steps are now always shown
    // for a Service or Leak Repair (only "No Repair Attempted" hides them) and capture the
    // verification outcome independently of whether a leak existed at discovery — nulling them
    // wiped legitimately-entered data on Complete (the IVT step appeared to clear and didn't save).
    //
    // Clone the nested objects rather than mutating in place: `returnObject` shares its nested
    // object references with the caller's formState (shallow spread), so in-place mutation here
    // would also blank the live React state and visibly clear the step fields.
    if (!returnObject.initialLeakInspection?.wasLeakFound) {
        returnObject.initialLeakInspection = {
            ...returnObject.initialLeakInspection,
            inspectionDate: null,
            leakLocationCategoryId: null,
            leakLocationId: null,
            faultCauseTypeId: null,
            estimatedLeakAmount: null,
        };
    }

    return returnObject;
};

export const formatDataInstallReinstall = (data_obj) => {
    const {
        serviceActionId = null,
        wasProperVacuumPulled = null,
        vacuumPsi = null,
        finalCoolingApplianceStatusId = null,
        partsRequired = null,
        verificationLeakDetectionMethodId,
        chargeCapacitySourceTypeId,
        initialLeakInspection,
        initialVerificationLeakInspection,
        verificationLeakInspection,
        wasProperVacuumPulledInt,
        assetDetails,
        finalModalOpen,
        installGasModalOpen,
        ...rest
    } = data_obj;
    const coolingApplianceService = {
        serviceActionId,
        wasProperVacuumPulled,
        vacuumPsi,
        finalCoolingApplianceStatusId,
        partsRequired,
        verificationLeakDetectionMethodId,
        chargeCapacitySourceTypeId,
    };

    return { ...rest, coolingApplianceService };
};

export const formatDataMaintenanceNoGas = (data_obj) => {
    const {
        assetDetails,
        finalModalOpen,
        gasChargeCapacity,
        initialLeakInspection,
        initialVerificationLeakInspection,
        installs,
        recoveries,
        repairNotes,
        userWeightUnits,
        verificationLeakInspection,
        workItemTypeString,
        ...rest
    } = data_obj;

    return {
        ...rest
    };
};

export const formatDataRecordRefrigerant = (data_obj) => {
    const {
        assetDetails,
        finalModalOpen,
        gasChargeCapacity,
        initialVerificationLeakInspection,
        repairNotes,
        userWeightUnits,
        verificationLeakInspection,
        workItemTypeString,
        maintenanceNoGas,
        coolingApplianceStatusId,
        finalCoolingApplianceStatusId = null,
        ...rest
    } = data_obj;
    const coolingApplianceService = { finalCoolingApplianceStatusId };

    return { ...rest, coolingApplianceService };
};

export const formatDataMoveToNewLocation = (data_obj) => {
    const {
        assetDetails,
        finalModalOpen,
        gasChargeCapacity,
        initialLeakInspection,
        initialVerificationLeakInspection,
        repairNotes,
        userWeightUnits,
        verificationLeakInspection,
        workItemTypeString,
        maintenanceNoGas,
        coolingApplianceStatusId,
        finalCoolingApplianceStatusId,
        newLocationId,
        ...rest
    } = data_obj;
    const coolingApplianceService = { newLocationId };

    return { ...rest, coolingApplianceService };
};

// use this function for service event types that require the data to be mapped
export const formatFinalData = (serviceEventType, finalData, isSaveForLater = false) => {
    const serviceEventDataMap = {
        [`${Enums.SE.WorkItemType.LeakInspection}`]: formatDataLeakInspection(finalData),
        [`${Enums.SE.WorkItemType.ServiceOrLeakRepair}`]: formatDataServiceOrLeakRepair(finalData, isSaveForLater),
        [`${Enums.SE.WorkItemType.Retrofit}`]: formatDataRetrofit(finalData),
        [`${Enums.SE.WorkItemType.ShutdownMothball}`]: formatDataShutdownMothball(finalData),
        [`${Enums.SE.WorkItemType.Install}`]: formatDataInstallReinstall(finalData),
        [`${Enums.SE.WorkItemType.Reinstall}`]: formatDataInstallReinstall(finalData),
        [`${Enums.SE.WorkItemType.MaintenanceNoGas}`]: formatDataMaintenanceNoGas(finalData),
        [`${Enums.SE.WorkItemType.RecordRefrigerant}`]: formatDataRecordRefrigerant(finalData),
        [`${Enums.SE.WorkItemType.Move}`]: formatDataMoveToNewLocation(finalData),
    };

    return serviceEventDataMap[serviceEventType] || finalData;
};

export const shouldFetchCylinders = (workItemTypeId) => [
    Enums.SE.WorkItemType.ServiceOrLeakRepair,
    Enums.SE.WorkItemType.ShutdownMothball,
    Enums.SE.WorkItemType.Retrofit,
    Enums.SE.WorkItemType.Install,
    Enums.SE.WorkItemType.Reinstall,
    Enums.SE.WorkItemType.RecordRefrigerant,
].includes(workItemTypeId);

export const initialDropdownOptions = {
    assets: [{ id: 1, name: "Loading ..." }],
    workItemTypes: [],
    savedWorkItemTypes: [],
    technicians: [],
    filteredTechs: [],
    leakCauses: [],
    leakLocationCategories: [],
    leakLocations: [],
    leakDetectionMethods: [],
    glCodes: [],
    locations: [],
    laborHours: [],
    repairStatuses: [],
    repairOutcomeTypes: [],
    transferReasons: [],
    coolingApplianceStatuses: [],
    materialTypes: [],
    chargeCapacitySourceTypes: [],
    recoveryEquipment: [],
    recoveryCylinders: [...unknownCylinder],
    installCylinders: [...unknownCylinder],
    serviceActions: [],
    afterServiceStatusOptions: [],
};

export const getFormattedDate = date => moment(date).format('MM/DD/YYYY');

export const getInitialErrorsExist = ({
    missingFields,
    requiredFieldsArray,
    nestedKey = "",
}) => {
    let filteredObject = {};

    // Nested object key having true as value indicates it is a missing field
    if (nestedKey) {
        let errorExists = false;
        filteredObject = missingFields[nestedKey];
        Object.keys(filteredObject).forEach((key) => {
            if (filteredObject[key] === true) {
                errorExists = true;
            }
        });
        return errorExists;
    }

    // Filters down missingFields object to only the specified keys
    filteredObject = Object.fromEntries(
        Object
        .entries(missingFields)
        .filter(([key]) => requiredFieldsArray.includes(key))
    );

    return Object.values(filteredObject).includes(true);
};

export const repairDetailsAddRequiredFields = (currentRequiredFields) => ({
    ...currentRequiredFields,
    "RepairDetails": [
        "leakDetectionMethodId",
        "wasLeakFound",
        "inspectionDate",
        "leakLocationId",
        "faultCauseTypeId",
    ]
});

export const repairNotesAddRequiredFields = (currentRequiredFields) => ({
    ...currentRequiredFields,
    "RepairNotes": [
        ...currentRequiredFields["RepairNotes"],
        "repairOutcomeTypeId",
    ]
});

export const initialVerificationTestAddRequiredFields = (currentRequiredFields) => ({
    ...currentRequiredFields,
    "InitialVerificationTest": [
        "leakDetectionMethodId",
        "wasLeakFound",
        "inspectionDate",
    ]
});

export const followUpVerificationServiceAddRequiredFields = (currentRequiredFields) => ({
    ...currentRequiredFields,
    "FollowUpVerificationService": [
        "inspectionDate",
        "leakDetectionMethodId",
        "wasLeakFound",
        "leakLocationId",
        "faultCauseTypeId",
    ]
});

// weightValue is always in lbs; returns lbs or kg depending on the user's unit preference.
export const getConvertedWeight = (weightValue, lbsWeightPrecision = 3, kgsWeightPrecision = 2) => {
    const badValues = [undefined, null, ""];
    if (isNaN(weightValue) || badValues.includes(weightValue)) return null;
    
    const usersWeightUnits = UserInfo.getWeightUnits();
    // Only convert to kgs when the user has EXPLICITLY chosen Kgs. Any other value —
    // 'Lbs', '', null, or undefined (preference never set / "Display weights in" blank) —
    // defaults to lbs, the unit weights are stored in. The previous check only matched the
    // exact empty string, and via && / || precedence ('A || B && C' === 'A || (B && C)') an
    // unset preference stored as null/undefined fell through to the kgs branch — converting
    // the asset capacity to kgs while the entered amount stayed in lbs, producing false
    // "Install amount cannot exceed N kgs" validation errors (Bug 14817).
    if (usersWeightUnits !== 'Kgs') {
        return parseFloat(Number(weightValue).toFixed(lbsWeightPrecision));
    }
    const kg = 0.453592;
    return parseFloat((weightValue * kg).toFixed(kgsWeightPrecision));
};
export const formatLocaleDate = (date) => {
  if (!date) return '';
  return moment(date).local().format('MM/DD/YYYY, h:mm A');
};

export const getContainerSpacing = () => {
    if (isMobile) {
        return 2;
    }
    return 4; // Tablet & Desktop
};

// ── SearchableDropdown renderOption helpers & components ──

/**
 * Truncates a string using one of two strategies:
 *
 * **Ellipsis** (default) — cuts at `max` characters and appends "…"
 *   truncate('Long asset name here', { max: 12 })  →  "Long asset n…"
 *
 * **Middle collapse** — shows the first `start` and last `end` characters with "…" in between.
 *   Provide `start` and `end` to enable this mode.
 *   truncate('ABCDE12345FGHIJ67890', { max: 15, start: 5, end: 5 })  →  "ABCDE…67890"
 *
 * @param {string} str - The string to truncate.
 * @param {Object} options
 * @param {number} options.max - Maximum allowed length before truncation is applied.
 * @param {number} [options.start] - Characters to keep from the start (middle-collapse mode).
 * @param {number} [options.end] - Characters to keep from the end (middle-collapse mode).
 * @returns {string} The truncated string, or the original if within max length.
 */
export const truncate = (str, { max, start, end } = {}) => {
    if (!str || str.length <= max) return str || '';
    if (start !== undefined && end !== undefined) {
        return str.slice(0, start) + '\u2026' + str.slice(-end);
    }
    return str.slice(0, max).trimEnd() + '\u2026';
};

const OP_STATUS_COLORS = {
    'active': Grey3,
    'inactive': '#e65100',
    'disposed/destroyed': 'rgba(26, 23, 59, 0.38)',
};

const getOpStatusColor = (status) => {
    if (!status) return Grey3;
    return OP_STATUS_COLORS[status.toLowerCase()] ?? Grey3;
};

const highlightMatch = (text, match) => {
    if (!match || !text) return text;
    const idx = text.toLowerCase().indexOf(match.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <span style={{ background: 'rgba(0, 133, 155, 0.18)', fontWeight: 700, borderRadius: 2 }}>
                {text.slice(idx, idx + match.length)}
            </span>
            {text.slice(idx + match.length)}
        </>
    );
};

export const EquipmentOption = (props, option, state) => {
    const term = state?.inputValue || '';
    return (
        <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: determineBadgeColor(option.assetStatus),
                        flexShrink: 0,
                        mt: '6px',
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        component="span"
                        sx={{
                            display: 'block',
                            fontFamily: trakrefFontFamilySemiBold,
                            fontSize: '14px',
                            color: 'rgba(26, 23, 59, 0.87)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {highlightMatch(truncate(option.name, { max: 20 }), term)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', mt: '2px' }}>
                        <Typography component="span" sx={{ fontSize: '11px', color: trakrefBlue, fontWeight: 600, fontFamily: trakrefFontFamily, whiteSpace: 'nowrap' }}>
                            {option.materialTypeName}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: '11px', color: '#e0e0e0', fontWeight: 300 }}>·</Typography>
                        <Typography component="span" sx={{ fontSize: '11px', color: Grey3, fontFamily: trakrefFontFamily, whiteSpace: 'nowrap' }}>
                            {highlightMatch(truncate(option.serialNumber || option.tagNumber, { max: 15, start: 5, end: 5 }), term)}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: '11px', color: '#e0e0e0', fontWeight: 300 }}>·</Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '11px',
                                fontStyle: 'italic',
                                fontFamily: trakrefFontFamily,
                                whiteSpace: 'nowrap',
                                color: getOpStatusColor(option.coolingApplianceStatusName),
                            }}
                        >
                            {option.coolingApplianceStatusName}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </li>
    );
};

export const TechnicianOption = (props, option, state) => {
    const term = state?.inputValue || '';
    const firstName = truncate(option.firstName, { max: 12 });
    const userName = truncate(option.userName, { max: 10, start: 3, end: 2 });
    return (
        <li {...props} key={option.id}>
            <Typography
                component="span"
                sx={{
                    fontFamily: trakrefFontFamily,
                    fontSize: '14px',
                    color: 'rgba(26, 23, 59, 0.87)',
                }}
            >
                {highlightMatch(option.lastName, term)}, {highlightMatch(firstName, term)} ({userName})
            </Typography>
        </li>
    );
};
